import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import DashboardLayout from '../../components/DashboardLayout';
import api from '../../utils/api';
import { Image, Newspaper, HelpCircle, Plus, Trash2, Pencil, X, Eye, EyeOff } from 'lucide-react';

const TABS = [
  { key: 'banner', label: 'Banners', icon: Image },
  { key: 'news', label: 'News', icon: Newspaper },
  { key: 'faq', label: 'FAQs', icon: HelpCircle },
];

const EMPTY_FORMS = {
  banner: { title: '', imageUrl: '', link: '', order: 0 },
  news: { title: '', content: '' },
  faq: { question: '', answer: '', order: 0 },
};

export default function AdminCMS() {
  const [tab, setTab] = useState('banner');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORMS.banner);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchItems = useCallback(() => {
    setLoading(true);
    api.get(`/cms/${tab}/admin`)
      .then(r => setItems(r.data.items))
      .catch(err => toast.error(err.response?.data?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [tab]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const switchTab = (t) => {
    setTab(t);
    setForm(EMPTY_FORMS[t]);
    setEditingId(null);
  };

  const editItem = (item) => {
    setEditingId(item._id);
    const { title, imageUrl, link, order, question, answer, content } = item;
    if (tab === 'banner') setForm({ title, imageUrl, link, order });
    else if (tab === 'news') setForm({ title, content });
    else setForm({ question, answer, order });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetForm = () => { setForm(EMPTY_FORMS[tab]); setEditingId(null); };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = editingId
        ? await api.put(`/cms/${tab}/${editingId}`, form)
        : await api.post(`/cms/${tab}`, form);
      toast.success(res.data.message);
      resetForm();
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await api.delete(`/cms/${tab}/${id}`);
      toast.success('Deleted');
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete');
    }
  };

  const toggle = async (id) => {
    try {
      await api.put(`/cms/${tab}/${id}/toggle`);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update status');
    }
  };

  const activeTabMeta = TABS.find(t => t.key === tab);

  return (
    <DashboardLayout>
      <h1 className="page-title">CMS Management</h1>
      <p className="page-subtitle">Manage homepage banners, news updates, and FAQ content</p>

      <div className="tab-bar" style={{ marginBottom: 16 }}>
        {TABS.map(t => (
          <button key={t.key} className={`tab-btn ${tab === t.key ? 'active' : ''}`} onClick={() => switchTab(t.key)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {/* Form */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header">
          <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Plus size={18} /> {editingId ? `Edit ${activeTabMeta.label.slice(0, -1)}` : `Add ${activeTabMeta.label.slice(0, -1)}`}
          </h3>
          {editingId && <button className="btn btn-outline btn-sm" onClick={resetForm}><X size={14} /> Cancel Edit</button>}
        </div>

        <form onSubmit={submit}>
          {tab === 'banner' && (
            <>
              <div className="grid-2" style={{ gap: 16 }}>
                <div className="form-group">
                  <label>Title</label>
                  <input type="text" className="form-control" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required />
                </div>
                <div className="form-group">
                  <label>Order (lower shows first)</label>
                  <input type="number" className="form-control" value={form.order} onChange={e => setForm(f => ({ ...f, order: Number(e.target.value) }))} />
                </div>
              </div>
              <div className="form-group">
                <label>Image URL</label>
                <input type="text" className="form-control" value={form.imageUrl} onChange={e => setForm(f => ({ ...f, imageUrl: e.target.value }))} placeholder="https://..." required />
              </div>
              <div className="form-group">
                <label>Link (optional — where the banner takes users)</label>
                <input type="text" className="form-control" value={form.link} onChange={e => setForm(f => ({ ...f, link: e.target.value }))} placeholder="https://... or /register" />
              </div>
            </>
          )}

          {tab === 'news' && (
            <>
              <div className="form-group">
                <label>Title</label>
                <input type="text" className="form-control" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label>Content</label>
                <textarea className="form-control" rows={5} value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} required />
              </div>
            </>
          )}

          {tab === 'faq' && (
            <>
              <div className="form-group">
                <label>Question</label>
                <input type="text" className="form-control" value={form.question} onChange={e => setForm(f => ({ ...f, question: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label>Answer</label>
                <textarea className="form-control" rows={4} value={form.answer} onChange={e => setForm(f => ({ ...f, answer: e.target.value }))} required />
              </div>
              <div className="form-group" style={{ maxWidth: 200 }}>
                <label>Order (lower shows first)</label>
                <input type="number" className="form-control" value={form.order} onChange={e => setForm(f => ({ ...f, order: Number(e.target.value) }))} />
              </div>
            </>
          )}

          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Saving...' : editingId ? 'Update' : 'Add'}
          </button>
        </form>
      </div>

      {/* List */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}><activeTabMeta.icon size={18} /> {activeTabMeta.label}</h3>
          <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{items.length} items</div>
        </div>

        {loading ? (
          <div className="loader-wrap"><div className="loader"></div></div>
        ) : items.length === 0 ? (
          <div className="empty-state"><div className="empty-icon"><activeTabMeta.icon size={40} /></div><h3>No {activeTabMeta.label.toLowerCase()} yet</h3></div>
        ) : (
          <div className="table-wrap">
            <table className="tp-table">
              <thead>
                <tr>
                  {tab === 'banner' && <><th>Preview</th><th>Title</th><th>Order</th></>}
                  {tab === 'news' && <><th>Title</th><th>Content</th><th>Published</th></>}
                  {tab === 'faq' && <><th>Question</th><th>Order</th></>}
                  <th>Status</th><th></th>
                </tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <tr key={item._id}>
                    {tab === 'banner' && (
                      <>
                        <td><img src={item.imageUrl} alt={item.title} style={{ width: 80, height: 40, objectFit: 'cover', borderRadius: 6 }} onError={e => { e.target.style.display = 'none'; }} /></td>
                        <td style={{ fontWeight: 600 }}>{item.title}</td>
                        <td>{item.order}</td>
                      </>
                    )}
                    {tab === 'news' && (
                      <>
                        <td style={{ fontWeight: 600 }}>{item.title}</td>
                        <td style={{ fontSize: '0.82rem', color: 'var(--text-muted)', maxWidth: 320 }}>{item.content.slice(0, 80)}{item.content.length > 80 ? '…' : ''}</td>
                        <td style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{new Date(item.publishedAt).toLocaleDateString('en-IN')}</td>
                      </>
                    )}
                    {tab === 'faq' && (
                      <>
                        <td style={{ fontWeight: 600, maxWidth: 320 }}>{item.question}</td>
                        <td>{item.order}</td>
                      </>
                    )}
                    <td>
                      <button onClick={() => toggle(item._id)} className={`badge ${item.isActive ? 'badge-success' : 'badge-danger'}`} style={{ border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        {item.isActive ? <Eye size={12} /> : <EyeOff size={12} />} {item.isActive ? 'Active' : 'Hidden'}
                      </button>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button className="btn btn-outline btn-sm" style={{ marginRight: 6, padding: 6 }} onClick={() => editItem(item)}><Pencil size={14} /></button>
                      <button className="btn btn-outline btn-sm" style={{ padding: 6, color: '#ef4444' }} onClick={() => remove(item._id)}><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}